/* eslint-disable camelcase */
import { useEffect, useState } from 'react';
import { Button, Dropdown, Nav, Tab } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { CLOUD, COCKPIT } from '../../../config/constants';
import { getCloudsAUserHasAccessTo } from '../../../redux/slices/cloud/cloudsList';
import { getLocationsAUserHasAccessTo } from '../../../redux/slices/location/locationsList';
import SelectLocationsTable from './table';
import PermissionsTable from './permissions';
import * as PropTypes from 'prop-types';
import {
  ACCESS_TYPES,
  ACCESS_TYPE_ALL,
  ACCESS_TYPE_SELECTED,
  ROLE_ADMIN,
  ROLE_GUEST
} from '../../../config/roles';
import { Toaster, toast } from 'react-hot-toast';
import { useTranslate } from '@tolgee/react';

export default function SelectLocationsComponent({
  cloudRole,
  cockpitRole,
  selectedLocations,
  setSelectedLocations,
  selectedClouds,
  setSelectedClouds,
  cloudAccessType,
  setCloudAccessType,
  cockpitAccessType,
  setCockpitAccessType
}) {
  const dispatch = useDispatch();

  const { count: locationsCount, isLoading: cockpitIsLoading } = useSelector(
    (state) => state.locationsList
  );
  const { count: cloudsCount, isLoading: cloudsIsLoading } = useSelector(
    (state) => state.cloudsList
  );
  useEffect(() => {
    dispatch(getLocationsAUserHasAccessTo());
    dispatch(getCloudsAUserHasAccessTo());
  }, [dispatch]);

  const { hasSocialMediaManagementSubscription, hasCloudSubscription } =
    useSelector((state) => state.userDetails);
  const { t } = useTranslate();
  const [sectionName, setSectionName] = useState('permissions');
  useEffect(() => {
    // Check if cloudRole has become null
    if (cloudRole === null && sectionName === CLOUD) {
      // If so, navigate to the 'permissions' tab
      handleTabSelect('permissions');
    }
    if (cockpitRole === null && sectionName === COCKPIT) {
      handleTabSelect('permissions');
    }
  }, [cloudRole, cockpitRole]);

  useEffect(() => {
    if (cloudRole) {
      handleTabSelect(CLOUD);
    }
  }, [cloudRole]);
  useEffect(() => {
    // Check if cloudRole has become null
    if (cockpitRole) {
      // If so, navigate to the 'permissions' tab
      handleTabSelect(COCKPIT);
    }
  }, [cockpitRole]);

  const handleTabSelect = (selectedTab) => {
    setSectionName(selectedTab);
  };
  const handleClickForCockpit = (
    hasSocialMediaManagementSubscription,
    cockpitRole
  ) => {
    if (!hasSocialMediaManagementSubscription) {
      toast.error(
        t('invite.createForm.no_social_media_management_subscription'),
        {
          position: 'top-right'
        }
      );
    } else if (!cockpitRole) {
      toast.error(t('invite.createForm.please_select_cockpit_role'), {
        position: 'top-right'
      });
    }
  };

  const handleClickForCloud = (hasCloudSubscription, cockpitRole) => {
    if (!hasCloudSubscription) {
      toast.error(t('invite.createForm.no_cloud_subscription'), {
        position: 'top-right'
      });
    } else if (!cockpitRole) {
      toast.error(t('invite.createForm.please_select_cloud_role'), {
        position: 'top-right'
      });
    }
  };
  return (
    <div className="rounded card">
      <Toaster />
      <Tab.Container>
        <div className="border-0">
          <Nav variant="light" activeKey={sectionName} className="p-0 m-0">
            <Nav.Item>
              <Nav.Link eventKey="invites">
                <Button
                  variant="light"
                  size="sm"
                  className={`${
                    sectionName === 'permissions' ? 'btn-light-primary' : ''
                  }`}
                  onClick={() => handleTabSelect('permissions')}
                >
                  {t('permissions')}
                </Button>
              </Nav.Link>
            </Nav.Item>
            {hasSocialMediaManagementSubscription && (
              <Nav.Item>
                <Nav.Link
                  eventKey={COCKPIT}
                  onClick={() =>
                    handleClickForCockpit(
                      hasSocialMediaManagementSubscription,
                      cockpitRole
                    )
                  }
                >
                  <Button
                    disabled={
                      !hasSocialMediaManagementSubscription || !cockpitRole
                    }
                    variant="light"
                    size="sm"
                    className={`${
                      sectionName === COCKPIT ? 'btn-light-primary' : ''
                    }`}
                    onClick={() => handleTabSelect(COCKPIT)}
                  >
                    {t(COCKPIT)}
                    <div className="badge badge-light-success ms-2">
                      {!cockpitRole && '-'}
                      {cockpitRole &&
                        (cockpitAccessType === ACCESS_TYPE_ALL ||
                          cockpitRole === ROLE_ADMIN) &&
                        t('all')}
                      {cockpitRole &&
                        cockpitAccessType === ACCESS_TYPE_SELECTED &&
                        cockpitRole !== ROLE_ADMIN &&
                        !cockpitIsLoading &&
                        `${selectedLocations?.length} / ${locationsCount} `}
                    </div>
                  </Button>
                </Nav.Link>
              </Nav.Item>
            )}
            {hasCloudSubscription && (
              <Nav.Item>
                <Nav.Link
                  eventKey={CLOUD}
                  onClick={() =>
                    handleClickForCloud(hasCloudSubscription, cloudRole)
                  }
                >
                  <Button
                    disabled={!hasCloudSubscription || !cloudRole}
                    variant="light"
                    size="sm"
                    className={`${
                      sectionName === CLOUD ? 'btn-light-primary' : ''
                    }`}
                    onClick={() => handleTabSelect(CLOUD)}
                  >
                    {t(CLOUD)}
                    {
                      <div className="badge badge-light-success ms-2">
                        {!cloudRole && '-'}
                        {cloudRole &&
                          (cloudAccessType === ACCESS_TYPE_ALL ||
                            cloudRole === ROLE_ADMIN) &&
                          t('all')}
                        {cloudRole &&
                          cloudAccessType === ACCESS_TYPE_SELECTED &&
                          cloudRole !== ROLE_ADMIN &&
                          !cloudsIsLoading &&
                          `${selectedClouds?.length} / ${cloudsCount}`}
                      </div>
                    }
                  </Button>
                </Nav.Link>
              </Nav.Item>
            )}
          </Nav>
        </div>
        <div className="card-body">
          <Tab.Content>
            <Tab.Pane eventKey="invites" active={sectionName === 'permissions'}>
              <PermissionsTable />
            </Tab.Pane>
            <Tab.Pane
              eventKey={COCKPIT}
              active={sectionName === COCKPIT && cockpitRole}
            >
              <div>
                <div className="d-flex flex-end">
                  <AccessTypeDropdown
                    onChange={setCockpitAccessType}
                    value={cockpitAccessType}
                    role={cockpitRole}
                  />
                </div>

                <SelectLocationsTable
                  setSelectedRows={setSelectedLocations}
                  selectedRows={selectedLocations}
                  type={COCKPIT}
                  allRowsSelected={
                    cockpitAccessType === ACCESS_TYPE_ALL ||
                    cockpitRole === ROLE_ADMIN
                  }
                />
              </div>
            </Tab.Pane>
            <Tab.Pane
              eventKey={CLOUD}
              active={sectionName === CLOUD && cloudRole}
            >
              {cockpitRole !== ROLE_GUEST ? (
                <div>
                  <div className="d-flex flex-end">
                    <AccessTypeDropdown
                      onChange={setCloudAccessType}
                      value={cloudAccessType}
                      role={cloudRole}
                    />
                  </div>

                  <SelectLocationsTable
                    setSelectedRows={setSelectedClouds}
                    selectedRows={selectedClouds}
                    type={CLOUD}
                    allRowsSelected={
                      cloudAccessType === ACCESS_TYPE_ALL ||
                      cloudRole === ROLE_ADMIN
                    }
                  />
                </div>
              ) : (
                <div
                  className="text-center d-flex justify-content-center align-items-center"
                  style={{ height: 370 }}
                >
                  {t('guest_has_no_access_to_clouds')}
                </div>
              )}
            </Tab.Pane>
          </Tab.Content>
        </div>
      </Tab.Container>
    </div>
  );
}

SelectLocationsComponent.propTypes = {
  cloudRole: PropTypes.any,
  cockpitRole: PropTypes.any,
  selectedLocations: PropTypes.any,
  selectedClouds: PropTypes.any,
  setSelectedClouds: PropTypes.any,
  setSelectedLocations: PropTypes.any,
  cloudAccessType: PropTypes.any,
  setCloudAccessType: PropTypes.any,
  cockpitAccessType: PropTypes.any,
  setCockpitAccessType: PropTypes.any
};

const AccessTypeDropdown = ({ value, onChange, role }) => {
  const { t } = useTranslate();
  useEffect(() => {
    if (role === ROLE_GUEST) onChange(ACCESS_TYPE_SELECTED);
  }, [role]);
  return (
    <Dropdown disabled={!role || role === ROLE_GUEST}>
      <Dropdown.Toggle
        variant="light"
        id="dropdown-basic"
        disabled={!role || role === ROLE_GUEST}
      >
        {value
          ? t(`config.selection_type.${ACCESS_TYPES[value]}`)
          : t('config.selection_type.loading')}
      </Dropdown.Toggle>
      <Dropdown.Menu>
        {Object.keys(ACCESS_TYPES).map((id) => (
          <Dropdown.Item
            key={id}
            value={Number(id)}
            active={value === Number(id)}
            onClick={() => onChange(Number(id))}
          >
            {t(`config.roles.${ACCESS_TYPES[id]}`)}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

AccessTypeDropdown.propTypes = {
  value: PropTypes.number,
  onChange: PropTypes.any,
  role: PropTypes.any
};
